import { Input } from '@angular/core';
import { ConsolidatedPhoneReportComponent } from '../consolidated-phone-report/consolidated-phone-report.component';
import { DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ClipboardService } from 'ngx-clipboard';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { HttpService } from 'src/app/http.service';
import { filterBanksForDropdown, settingIsOn } from 'src/app/util/bank-usage.util';
import { PlayerFormComponent } from '../player-form/player-form.component';
import { PlayerTransactionHistoryComponent } from '../player-transaction-history/player-transaction-history.component';
import { ManualCreatePlayerComponent } from '../manual-create-player/manual-create-player.component';
import { PlayerDailyComponent } from '../player-daily/player-daily.component';
import { UnknownPaymentFormComponent } from '../unknown-payment-form/unknown-payment-form.component';
import { PlayerProfitComponent } from '../player-profit/player-profit.component';
import { PlayerRemoveComponent } from '../player-remove/player-remove.component';
import { LuckyDrawModalComponent } from '../lucky-draw-modal/lucky-draw-modal.component';
import { CommissionModalComponent } from '../commission-modal/commission-modal.component';

@Component({
  selector: 'app-manual-topup-withdraw',
  templateUrl: './manual-topup-withdraw.component.html',
  styleUrls: ['./manual-topup-withdraw.component.scss']
})
export class ManualTopupWithdrawComponent implements AfterViewInit {
  @Input() consolidatedReportCompanyUuid = '';
  private tenantReportCompanyUuid = '';
  get effectiveReportCompanyUuid() {
    return this.consolidatedReportCompanyUuid || this.tenantReportCompanyUuid;
  }
  openConsolidatedReport() {
    const modal = this.ngbModal.open(ConsolidatedPhoneReportComponent, {size:'lg'});
    modal.componentInstance.playerUuid = this.player.uuid;
    modal.componentInstance.reportCompanyUuid = this.effectiveReportCompanyUuid;
  }

  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('search') search;
  @ViewChild("selectPlayerGameID") selectPlayerGameID: NgSelectComponent
  @ViewChild("payout") payout: NgSelectComponent
  @ViewChild("th") th: PlayerTransactionHistoryComponent;
  @ViewChild("th") th1: PlayerTransactionHistoryComponent;

  public show_game_balance = false;
  public is_copy: boolean = false;
  public is_loading: any = false
  public is_loading_function: any = false
  public is_searching_player: any = false
  public is_loading_game: any = false
  public is_loading_player_game_id = false;
  public player_search;
  public company_uuid;
  public game_balance: any;
  public data: any = { process: 'topup' };
  public players: any = [];
  public games: any = [];
  public transfer_games: any = []
  public transfer_player_ids: any = []

  public banks: any = [];
  // Deposit vs withdraw bank selectors are split by the setting_bank
  // use_deposit/use_withdraw flags (see bank-usage.util). banks stays the full
  // list (Bank Summary); these two feed the section-specific dropdowns.
  public depositBanks: any = [];
  public withdrawBanks: any = [];
  public player_banks: any = [];
  public player_ids: any = []
  public player: any = {};
  public copy_text: any;
  public copy_text_append: any;
  public referral_rate: any = 0;
  private current_company: any = {}
  public setting: any = {}
  public tenant_companies: any[] = [];
  public tenant_grouped = false;
  public tenant_report_name = '';
  public tenant_error = '';
  public operation_company_uuid: any;
  public grantedPermissions: { [path: string]: any } = {};
  private playerSelectionVersion = 0;

  constructor(private http: HttpService, private ngbModal: NgbModal, private clipboardApi: ClipboardService, private route: ActivatedRoute) {
    this.company_uuid = this.http.getStaticSelectedCompany();
    this.operation_company_uuid = this.company_uuid;
    this.current_company = this.http.getSelecetdCompanyDetail()
    this.loadTenantContext();
    this.loadPermissions();
    this.companySetting();
  }

  private tenantPayload(data: any = {}, targetCompanyUuid: any = this.operation_company_uuid) {
    return {
      ...(data || {}),
      company_uuid: targetCompanyUuid || this.company_uuid,
      manual_tenant_origin_uuid: this.company_uuid
    };
  }

  private tenantPost(path: string, data: any = {}, targetCompanyUuid: any = this.operation_company_uuid): Promise<any> {
    return this.http.post(path, this.tenantPayload(data, targetCompanyUuid));
  }

  async loadPermissions() {
    // The permission that unlocks All Companies belongs to the company selected
    // in the sidebar (the origin), not to whichever player's company is open.
    const permission: any = await this.tenantPost('/admin/user/permission/hidden', {}, this.company_uuid);
    this.grantedPermissions = permission && typeof permission === 'object' ? permission : {};
  }

  can(path: string) {
    return !!this.grantedPermissions[path];
  }

  get tenantRequestContext() {
    return this.tenantPayload({});
  }

  async loadTenantContext() {
    const context: any = await this.http.post('/admin/manual-tenant/context', {
      company_uuid: this.company_uuid,
      manual_tenant_origin_uuid: this.company_uuid
    });
    this.tenant_companies = context && context.companies ? context.companies : [];
    this.tenant_grouped = context?.grouped === true;
    this.tenantReportCompanyUuid = context?.status === true && this.tenant_grouped
      && Array.isArray(context?.companies) ? context?.parent?.uuid || '' : '';
    this.tenant_report_name = context?.parent?.name || '';
    this.tenant_error = Array.isArray(context?.companies) ? '' : (context?.message || 'Unable to load report companies.');
  }

  isCrossTenant() {
    return this.operation_company_uuid !== this.company_uuid;
  }

  get operationCompanyName() {
    return this.player.operation_company_name ||
      this.tenant_companies.find((item: any) => item.uuid === this.operation_company_uuid)?.name ||
      this.current_company.name || '';
  }

  private operationCompanyReferralRate() {
    const company = this.tenant_companies.find((item: any) => item.uuid === this.operation_company_uuid);
    return company ? company.referral_rate : this.current_company.referral_rate;
  }

  ngAfterViewInit(): void {
    this.searchPlayerListener();
    // Deep-link support: the Seemless Wallet page links here with ?player=<uuid>
    // so the operator lands on this player already selected (no re-search).
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p: any) => {
      if (p && p.player && this.data.player_uuid !== p.player) {
        this.preselectPlayer(p.player);
      }
    });
  }

  async preselectPlayer(uuid: string) {
    try {
      if (!uuid) { return; }
      let y: any = await this.http.post("/admin/player/get/v2", { player_uuid: uuid });
      if (!y || !y.uuid) { return; }
      this.players = [y];
      await this.selectPlayer({ ...y, operation_company_uuid: this.company_uuid });
    } catch (e) {
      // fall back to manual search if the player can't be pre-loaded
    }
  }

  searchPlayerListener() {
    try {
      this.search.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
        .pipe(distinctUntilChanged())
        .subscribe((v: any) => {
          if (!v || v.length < 3) {
            return;
          }
          this.searchPlayer(v)
        })
    } catch (error) {}
  }

  resetPlayer() {
    ++this.playerSelectionVersion;
    this.data = { process: 'topup' };
    this.player = {};
    this.players = [];
    this.player_search = null;
    if (this.operation_company_uuid !== this.company_uuid) {
      this.operation_company_uuid = this.company_uuid;
      this.games = [];
      void this.loadPermissions();
      void this.companySetting();
    }
    setTimeout(() => { this.searchPlayerListener(); }, 100);
  }

  async companySetting(targetCompanyUuid: any = this.operation_company_uuid) {
    const y: any = await this.tenantPost("/admin/company/setting", {}, targetCompanyUuid);
    // A slow response for the previously selected company must not replace the
    // settings used by the player currently on screen.
    if (targetCompanyUuid === this.operation_company_uuid) {
      this.setting = y && y.setting ? y.setting : {};
    }
  }

  async searchPlayer(data: any) {
    delete this.data.player_uuid;
    this.is_searching_player = true;
    this.players = [];
    const result:any = await this.http.post("/admin/manual-tenant/player/search", {
      search: data,
      company_uuid: this.company_uuid,
      manual_tenant_origin_uuid: this.company_uuid
    }) as any[]
    this.players = Array.isArray(result) ? result : [];
    this.tenant_error = Array.isArray(result) ? '' : (result?.message || 'Unable to search report companies.');
    this.is_searching_player = false;
  }

  async selectPlayer(player: any) {
    const selectionVersion = ++this.playerSelectionVersion;
    this.player_search = null;
    const previousCompanyUuid = this.operation_company_uuid;
    this.operation_company_uuid = player.operation_company_uuid || this.company_uuid;
    const targetCompanyUuid = this.operation_company_uuid;
    this.data.player_uuid = player.uuid;
    this.data.player_game_id = null;
    this.data.game_id = null;

    this.player = player;

    if (previousCompanyUuid !== this.operation_company_uuid) {
      this.games = [];
      this.setting = {};
      await this.companySetting(targetCompanyUuid);
    }

    if (selectionVersion !== this.playerSelectionVersion) { return; }

    this.referral_rate = parseFloat(this.player.referral_rate ?? this.operationCompanyReferralRate());

    await this.getBankDetail(targetCompanyUuid, selectionVersion);
    await this.getGames(targetCompanyUuid, selectionVersion);

    if (selectionVersion !== this.playerSelectionVersion) { return; }

    this.th.initTable();
    this.th1.initTable();

  }

  async getBankDetail(targetCompanyUuid: any = this.operation_company_uuid, selectionVersion: number = this.playerSelectionVersion) {
    const y: any = await this.tenantPost("/admin/player/bank-detail", this.data, targetCompanyUuid)

    if (targetCompanyUuid !== this.operation_company_uuid || selectionVersion !== this.playerSelectionVersion) {
      return;
    }

    this.banks = y && Array.isArray(y.banks) ? y.banks : [];
    // Opt-in per company: hide "Do Not Show" (public=0) banks from the topup/withdraw
    // dropdowns while keeping the full list for the Bank Summary. Default OFF.
    const hideNonPublic = settingIsOn(this.setting && this.setting.panel_hide_nonpublic_banks);
    this.depositBanks = filterBanksForDropdown(this.banks, 'deposit', hideNonPublic);
    this.withdrawBanks = filterBanksForDropdown(this.banks, 'withdraw', hideNonPublic);
    this.player_banks = y && Array.isArray(y.player_banks) ? y.player_banks : [];
  }

  async getGames(targetCompanyUuid: any = this.operation_company_uuid, selectionVersion: number = this.playerSelectionVersion) {

    if (this.games && this.games.length > 0) {
      return;
    }

    this.is_loading_game = true;
    const games = await this.tenantPost("/admin/manual-game", {}, targetCompanyUuid) as any[]

    if (targetCompanyUuid === this.operation_company_uuid && selectionVersion === this.playerSelectionVersion) {
      this.games = Array.isArray(games) ? games : [];
    }

    this.is_loading_game = false;
  }

  async getPlayerGameID() {
    this.copy_text_append = "";
    this.is_loading_player_game_id = true;

    this.changePlayerGameID();

    this.player_ids = await this.tenantPost("/admin/player-game-id-by-game-id", { game_id: this.data.game_id, player_uuid: this.data.player_uuid }) as any[]

    let f = this.games.find((v: any) => {
      return v.id == this.data.game_id;
    })

    if (f.copy_text) {
      this.copy_text_append = f.copy_text;
    }

    this.transfer_games = this.games.filter((v: any) => {
      return v.id != this.data.game_id;
    })

    this.is_loading_player_game_id = false;
  }

  async addNewPlayerIDForm() {

    delete this.data.username;
    delete this.data.password;
    delete this.data.player_status;
    delete this.data.player_game_id;
    let y = this.ngbModal.open(ManualCreatePlayerComponent, { size: "xs" });
    this.is_loading_player_game_id = true;

    if (this.data.player_game_id) {
      let player: any = this.player_ids.find((v: any) => {
        return v.id === this.data.player_game_id
      })

      if (player) {

        this.data.username = player.username
        this.data.password = player.password
        this.data.player_status = player.status
      }
    }

    y.componentInstance.setData = this.tenantPayload(this.data);
    y.componentInstance.getPlayerGameID.subscribe((v: any) => {
      this.data.player_game_id = v;
      this.is_loading_player_game_id = false;
      this.getPlayerGameID();
      y.close();
    })
    y.dismissed.subscribe(() => {
      this.is_loading_player_game_id = false;
    })
  }

  changePlayerGameID() {
    this.game_balance = null;
    this.show_game_balance = false;
  }

  createNewAccount() {
    this.data.withdraw_to_account = "new"
    this.payout.close()
  }

  async updateGameBalance() {
    this.data.withdraw = this.game_balance;
  }

  async topup() {
    if (this.is_loading_function) return;

    if ((!this.data.amount || this.data.amount <= 0) && !this.data.bonus) {
      this.data.amount = null;
      this.http.notify("Deposit Account or bonus be empty")
      return;
    }

    if (!this.data.deposit_bank && !this.data.topup_only) {
      this.http.notify("Select Deposit Account")
      return;
    }

    this.changePlayerGameID();

    this.is_loading_function = true;

    let y: any = await this.tenantPost("/admin/manual-game/topup", this.data);
    this.is_loading_function = false;

    if (!y.status && y.message) {
      this.http.notify(y.message)
      return;
    }

    await this.generateCopy("Deposit: " + this.data.amount + "\nBonus: " + this.data.bonus + "\n")

    this.data = { game_id: this.data.game_id, player_uuid: this.data.player_uuid, player_game_id: this.data.player_game_id }
    this.data.player_game_id = null;
    this.data.game_id = null;

    await this.getBankDetail();
    await this.getGames();

    this.th.initTable();
    this.th1.initTable();
  }


  async withdraw() {
    if (this.is_loading_function) return;

    if (!this.data.full_withdraw && ((!this.data.withdraw || this.data.withdraw < 0) && this.data.withdraw !== 0)) {
      this.http.notify("Withdraw amount is required.")
      return;
    }

    if (!this.data.withdraw_only && (!this.data.withdraw_to_account || !this.data.withdraw_from_account)) {
      this.http.notify("Select Account to Withdraw or create new account")
      return;
    }

    if (!this.data.withdraw_only && (this.data.withdraw_to_account == 'new' && (!this.data.withdraw_bank || !this.data.withdraw_account_no))) {
      this.http.notify("Withdraw Bank Name & Account number are required.")
      return;
    } else {
    }

    if (!this.data.withdraw_only && this.data.enabled_finance_charge && this.data.finance_charge == undefined) {
      this.http.notify("Finance charge option are required.")
      return;
    }

    this.changePlayerGameID();

    this.is_loading_function = true;

    let y: any = await this.tenantPost("/admin/manual-game/withdraw", this.data);

    this.is_loading_function = false;

    if (!y.status && y.message) {
      this.http.notify(y.message)
      return;
    }

    await this.generateCopy("Withdraw: " + y.amount + "\n")
    this.data = { game_id: this.data.game_id, player_uuid: this.data.player_uuid, player_game_id: this.data.player_game_id }
    this.data.player_game_id = null;
    this.data.game_id = null;

    this.th.initTable();
    this.th1.initTable();
    await this.getBankDetail();
    await this.getGames();
  }

  async resetPassword() {
    this.is_loading_function = true;

    let y: any = await this.tenantPost("/admin/integrated-game/reset-password", this.data);

    this.is_loading_function = false;

    if (!y.status && y.message) {
      this.http.notify(y.message)
      return;
    }

    this.getPlayerGameID();

  }

  async disabledID() {

    this.changePlayerGameID();

    this.is_loading_function = true;

    let y: any = await this.tenantPost("/admin/integrated-game/disabled-id", this.data);

    this.is_loading_function = false;

    if (!y.status && y.message) {
      this.http.notify(y.message)
      return;
    }

    this.data.player_game_id = null;
    this.getPlayerGameID();

  }

  playerProfit() {
    if (this.effectiveReportCompanyUuid) { this.openConsolidatedReport(); return; }
    let modal: any = this.ngbModal.open(PlayerProfitComponent, { size: "md" });
    modal.componentInstance.setPlaterUUID = this.player.uuid
  }

  async copy() {

    if (this.copy_text) {
      this.copy_text = null
    } else {
      this.generateCopy();
    }
    this.is_copy = false;
  }

  async generateCopy(addon: any = false) {

    this.is_loading_function = true;

    let str: any = ""

    let date = new Date();
    // str += "Date: " + ("0" + date.getDate()).slice(-2) + "-" + ("0" + (date.getMonth() + 1)).slice(-2) + "-" + date.getFullYear() + " " + date.getHours() + ":" + date.getMinutes() + ":" + date.getSeconds() + "\n";

    // str += "Player:" + this.player.name + "\n";
    str += "Code:" + this.player.code + "\n";
    let game = this.games.find((v: any) => {
      return v.id == this.data.game_id
    })

    str += "Game: " + game.title + "\n"
    let player = this.player_ids.find((v: any) => {
      return v.id == this.data.player_game_id;
    });

    str += "ID: " + player.username + "\n"
    str += "PW: " + player.password + "\n"

    if (addon) {
      str += addon
    }

    str += this.copy_text_append

    this.clipboardApi.copyFromContent(str)

    this.is_loading_function = false;

    if (addon) {
      this.is_copy = true;
    }

    this.copy_text = str;

  }

  addPlayer(player = null) {
    let modal: any = this.ngbModal.open(PlayerFormComponent, { size: "md", backdrop: 'static' });

    if (player && player.uuid) {
      modal.componentInstance.playerUUID = player.uuid;
    }

    modal.componentInstance.fullwidth = true;
    modal.componentInstance.successEvent.subscribe(async (v: any) => {
      this.player = v;
      this.data.player_uuid = v.player_uuid;
      this.referral_rate = parseFloat(v.referral_rate ?? this.current_company.referral_rate);
      await this.getBankDetail();
      await this.getGames();
      delete this.data.game_id
      modal.close();
    })

    modal.componentInstance.closeEvent.subscribe(async (v: any) => {
      modal.close();
    })

  }

  getDaily() {
    if (this.effectiveReportCompanyUuid) { this.openConsolidatedReport(); return; }
    let modal: any = this.ngbModal.open(PlayerDailyComponent, { size: "md", backdrop: 'static' });
    modal.componentInstance.setUserUUID = this.player.uuid
    modal.componentInstance.closeEvent.subscribe(() => {
      modal.close()
    })
  }

  getUnknownPayment() {
    let modal: any = this.ngbModal.open(UnknownPaymentFormComponent, { size: "md" });
    modal.componentInstance.modal = true;
    modal.componentInstance.saveEvent.subscribe((v: any) => {
      modal.close()
    })
  }

  topupValueChange() {
    this.data.referral_commission = (this.data.amount * this.referral_rate) / 100;

    if (this.setting.credit_calculation_method && this.setting.credit_calculation_method == 'auto') {
      this.data.point = (this.data.amount * parseFloat(this.setting.credit_calculation_rate)) / 100;
    }

  }

  withdrawValueChange() {
    if (this.setting.credit_calculation_method && this.setting.credit_calculation_method == 'auto') {
      this.data.withdraw_point = -1 * (this.data.withdraw * parseFloat(this.data.credit_calculation_rate)) / 100;
    }

    this.data.actual_pay_to_bank = this.data.withdraw
  }

  fcRateChange() {
    if (this.data.finance_charge == "true") {
      this.data.finance_charge_rate = this.setting.finance_charge_rate;
    } else {
      delete this.data.finance_charge_rate
    }
  }

  copyAccount() {

    let bank = this.player_banks.find((v: any) => {
      return v.id == this.data.withdraw_to_account
    })

    if (bank && bank.account) {
      this.clipboardApi.copyFromContent(bank.account)
    }
  }

  copyUsername() {
    let games = this.player_ids.find((v: any) => {
      return v.id == this.data.player_game_id
    })

    if (games && games.username) {
      this.clipboardApi.copyFromContent(games.username)
    }

  }

  topupOnlyChange() {
    if (this.data.topup_only) {
      delete this.data.amount
    }
  }

  removePlayerID() {
    let modal: any = this.ngbModal.open(PlayerRemoveComponent, { size: "xs" });
    console.log(this.player_ids)
    modal.componentInstance.player_ids = this.player_ids;
    modal.componentInstance.request_context = this.tenantRequestContext;
    modal.componentInstance.saveEvent.subscribe((v: any) => {
      modal.close()
      this.getPlayerGameID()
    })
  }

  luckyDraw() {
    let modal: any = this.ngbModal.open(LuckyDrawModalComponent, { size: "xs" });
    modal.componentInstance.player = this.player;
    modal.componentInstance.saveEvent.subscribe((v: any) => {
      modal.close()
    })
  }

  commissionModal() {
    let modal: any = this.ngbModal.open(CommissionModalComponent, { size: "xs" });
    modal.componentInstance.playerUUID = this.player.uuid;
    modal.componentInstance.saveEvent.subscribe((v: any) => {
      modal.close()
    })
  }


  async transfer() {
    if (this.is_loading_function) return;
    if (!this.data.game_id || !this.data.player_game_id || !this.data.transfer_game_id || !this.data.transfer_player_game_id) {
      this.http.notify("Source and destination game accounts are required.");
      return;
    }
    if (String(this.data.player_game_id) === String(this.data.transfer_player_game_id)) {
      this.http.notify("Choose a different destination game account.");
      return;
    }
    if (!Number.isFinite(Number(this.data.transfer)) || Number(this.data.transfer) <= 0) {
      this.http.notify("Transfer amount must be a positive number.");
      return;
    }
    this.is_loading_function = true;
    try {
      const y: any = await this.tenantPost("/admin/integrated-game/manual-transfer", { ...this.data });
      if (!y || y.status !== true) {
        this.http.notify(y?.message || "Transfer result is unavailable. Check records before retrying.");
        return;
      }
      this.data.transfer_player_game_id = null;
      this.data.transfer_game_id = null;
      this.data.transfer = null;
      this.changePlayerGameID();
      this.th?.initTable();
      this.th1?.initTable();
      this.http.notify(y.message || "Transfer completed.");
    } catch (_) {
      this.http.notify("Transfer result is unavailable. Check records before retrying.");
    } finally {
      this.is_loading_function = false;
    }
  }


  async getTransferPlayerGameID() {

    this.is_loading_player_game_id = true;

    this.transfer_player_ids = await this.tenantPost("/admin/player-game-id-by-game-id", { game_id: this.data.transfer_game_id, player_uuid: this.data.player_uuid, status: 'all' }) as any[]

    this.is_loading_player_game_id = false;

  }
}
